/**
 * Chakra 7 — Sahasrara (§12). Element Bewustzijn, kleur violet/wit, mantra AUM
 * (963Hz). Resource-label: Verbinding (`block` voedt hier de Verbinding-meter).
 * Wereld: "De Verbroken Verbinding".
 *
 * Geen eigen meesterschapskaart (laatste chakra). Theluun (eindbaas) heeft geen
 * HP maar een Verbinding-meter (§13): `verbindingFight: true`. De automatische
 * −5/beurt, win ≥100 / verlies ≤0, en de MEGA COMBO (§13) zitten in de engine.
 */
import type { Chakra } from './types';
import { HEADERS } from './headers';

export const chakra7: Chakra = {
  header: HEADERS[7],
  wereld: 'De Verbroken Verbinding',
  codex:
    'Sahasrara, "duizendvoudig", zit boven op de kruin. Element Bewustzijn, kleur violet/wit, ±1000 bloemblaadjes (symbolisch voor onbegrensd), mantra stilte/AUM (963Hz). Levensfase: volwassenheid en ouderdom, zingeving voorbij het persoonlijke. Levensvraag: ben ik verbonden met iets groters dan mezelf? Lichamelijk: hersenen als geheel, pijnappelklier, zenuwstelsel. Tekort: cynisme, materialisme, gevoel van zinloosheid, afgesneden van iets groters. Teveel: spirituele ontkenning van het lichaam/de wereld, "boven het leven staan" als vlucht, escapisme onder het mom van verlichting. Balans: verwondering, zingeving zonder dogma, verbondenheid met het leven terwijl je er middenin staat — niet erboven. Herstel: stilte, dankbaarheid, natuur, loslaten van controle.',

  kaarten: [
    { id: 'ch7-seleniet', naam: 'Seleniet', type: 's', cost: 1, origin: 7, tekst: '+3 verbinding, neutraliseert nihilisme', effect: { block: 3, cleanse: 'one' } },
    { id: 'ch7-heldere-kwarts', naam: 'Heldere Kwarts', type: 's', cost: 2, origin: 7, tekst: 'Steenkaarten +1 effect (3 beurten)', effect: { buffOutput: { amt: 1, turns: 3 } } },
    { id: 'ch7-charoit', naam: 'Charoit', type: 's', cost: 2, origin: 7, tekst: '+5 verbinding direct', effect: { block: 5 } },
    { id: 'ch7-sugiliet', naam: 'Sugiliet', type: 's', cost: 3, origin: 7, tekst: 'Elementkaarten -1 kosten (3 beurten)', effect: { costDownType: { type: 'e', amt: 1, turns: 3 } } },
    { id: 'ch7-bergkristal', naam: 'Bergkristal', type: 's', cost: 3, origin: 7, tekst: 'Alle Sahasrara-kaarten ×2', effect: { mult: { origin: 7, amt: 2 } } },
    { id: 'ch7-pijnappelklier', naam: 'Pijnappelklier', type: 'o', cost: 2, origin: 7, tekst: '+8 verbinding', effect: { block: 8 } },
    { id: 'ch7-hersenen-eenheid', naam: 'Hersenen Eenheid', type: 'o', cost: 2, origin: 7, tekst: 'Volgende kaart +4 effect', effect: { buffNext: 4 } },
    { id: 'ch7-zenuwstelsel', naam: 'Zenuwstelsel', type: 'o', cost: 1, origin: 7, tekst: 'Neutraliseert scheidingsaanvallen', effect: { cleanse: 'one' } },
    { id: 'ch7-kruin-activatie', naam: 'Kruin Activatie', type: 'o', cost: 3, origin: 7, tekst: 'Elementkaarten gratis deze beurt', effect: { freeType: { type: 'e' } } },
    { id: 'ch7-diepe-meditatie', naam: 'Diepe Meditatie', type: 'p', cost: 2, origin: 7, tekst: 'Zolang actief: +3 verbinding/beurt', effect: { aura: { block: 3, name: 'Diepe Meditatie' } } },
    { id: 'ch7-stilte', naam: 'Stilte', type: 'p', cost: 1, origin: 7, tekst: '+5 verbinding, baas slaat aanval over', effect: { block: 5, bossSkip: true } },
    { id: 'ch7-vasten', naam: 'Vasten', type: 'p', cost: 2, origin: 7, tekst: '+8 verbinding, -2 energie', effect: { block: 8, energy: -2 } },
    { id: 'ch7-dankbaarheid', naam: 'Dankbaarheid', type: 'p', cost: 1, origin: 7, tekst: 'Trek 2 kaarten, +3 verbinding', effect: { draw: 2, block: 3 } },
    { id: 'ch7-overgave', naam: 'Overgave', type: 'p', cost: 3, origin: 7, tekst: 'Alle blokkades weg, +10 verbinding', effect: { cleanse: 'all', block: 10 } },
    { id: 'ch7-bewustzijn-element', naam: 'Bewustzijn Element', type: 'e', cost: 3, origin: 7, tekst: 'Alle Sahasrara-kaarten ×2', effect: { mult: { origin: 7, amt: 2 } } },
    { id: 'ch7-ware-verbinding', naam: 'Ware Verbinding', type: 'e', cost: 4, origin: 7, tekst: '+20 verbinding direct', effect: { block: 20 } },
    { id: 'ch7-aum-mantra', naam: 'AUM Mantra', type: 'm', cost: 2, origin: 7, tekst: '+15 verbinding, neutraliseert scheiding', effect: { block: 15, cleanse: 'one', tone: 963 } },
    { id: 'ch7-aum-verdiept', naam: 'AUM Verdiept', type: 'm', cost: 4, origin: 7, tekst: '+25 verbinding, alle blokkades weg', effect: { block: 25, cleanse: 'all', tone: 963 } },
    { id: 'ch7-violet-visualisatie', naam: 'Violet Visualisatie', type: 'k', cost: 1, origin: 7, tekst: '+3 verbinding, elementkaarten +1', effect: { block: 3, buffNext: 1 } },
    { id: 'ch7-witte-licht-adem', naam: 'Witte Licht Adem', type: 'k', cost: 2, origin: 7, tekst: 'Trek 2 kaarten, +5 verbinding', effect: { draw: 2, block: 5 } },
  ],

  bazen: [
    {
      id: 'ch7-kethon',
      naam: 'Kethon',
      titel: 'De Cynicus',
      hp: 30,
      energie: 3,
      aanvallen: [
        { naam: 'Alles Is Niks', cost: 2, effect: { status: { kind: 'nullifyType', type: 'p', turns: 1 } } },
        { naam: 'Wetenschappelijk Bewijs', cost: 3, effect: { status: { kind: 'costUp', type: 'e', amt: 3, turns: 1 } } },
        { naam: 'Nihilisme Golf', cost: 3, effect: { dmg: 5, energyLoss: 2 } },
      ],
      zwaktes: [
        { card: 'Helder Zien', counters: 'Alles Is Niks' },
        { card: 'Stilte', counters: '*' },
        { card: 'AUM Mantra', counters: 'Nihilisme Golf' },
      ],
    },
    {
      id: 'ch7-seluun',
      naam: 'Seluun',
      titel: 'De Materialist',
      hp: 35,
      energie: 3,
      aanvallen: [
        { naam: 'Meer Meer Meer', cost: 3, effect: { conditionalDmg: { amt: 4, condition: 'maxCostNotPlayed' } } },
        { naam: 'Bezit Boven Zijn', cost: 2, effect: { status: { kind: 'costUp', type: 'p', amt: 3, turns: 1 } } },
        { naam: 'Leegte van Prestatie', cost: 3, effect: { dmg: 6 } },
      ],
      zwaktes: [
        { card: 'Seleniet', counters: 'Meer Meer Meer' },
        { card: 'Vasten', bossEnergyLoss: 2 },
        { card: 'Bewustzijn Element', bonusWeaken: 2 },
      ],
    },
    {
      id: 'ch7-aruvaan',
      naam: 'Aruvaan',
      titel: 'De Afgescheidene',
      hp: 35,
      energie: 3,
      aanvallen: [
        { naam: 'Kosmische Onverschilligheid', cost: 3, effect: { status: { kind: 'debuffOutput', amt: 2, turns: 1 } } },
        { naam: 'Ego Transcendentie (Vals)', cost: 3, effect: { status: { kind: 'onlyType', type: 's', turns: 1 } } },
        { naam: 'Verbinding Verbreken', cost: 4, effect: { shuffleOriginCardsBack: { excludeOrigin: 7 } } },
      ],
      zwaktes: [
        { card: 'Open Hart', counters: 'Verbinding Verbreken' },
        { cards: ['Aardeanker', 'AUM Mantra'], bonusDmg: 12, sameTurn: true },
        { handHasAllOrigins: true, bonusDmg: 15 },
      ],
    },
    {
      id: 'ch7-theluun',
      naam: 'Theluun',
      titel: 'De Grote Scheiding',
      energie: 5,
      eindbaas: true,
      verbindingFight: true,
      passief:
        'Geen HP — Verbinding-meter (start 50, schaal 0–100; win bij ≥100, verlies bij ≤0). Elke baas-beurt automatisch −5 Verbinding. Block-kaarten storten hun waarde rechtstreeks in Verbinding.',
      aanvallen: [
        { naam: 'Scheiding Uitstralen', cost: 3, effect: { verbinding: -10 } },
        { naam: 'Betekenis Onttrekken', cost: 4, effect: { status: { kind: 'halfEffect', turns: 2 } } },
        { naam: 'Kosmische Stilte', cost: 5, effect: { status: { kind: 'nullifyAll', turns: 1 } } },
      ],
      zwaktes: [{ cards: ['Seleniet', 'Stilte'], counters: 'Kosmische Stilte', sameTurn: true }],
      leermoment:
        'Theluun is geen vijand die je verslaat, maar een afstand die je overbrugt. Sahasrara leert dat verbinding geen prestatie is — het is een keuze die je elke dag opnieuw maakt: met jezelf, met anderen, met het leven zelf. Ware balans is niet het einde van de reis, maar het begin van een leven in verbinding.',
    },
  ],
};
